import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { esMX, type Dictionary } from './es-MX';
import { enUS } from './en-US';

export type Locale = 'es-MX' | 'en-US';

const DICTIONARIES: Record<Locale, Dictionary> = {
  'es-MX': esMX,
  'en-US': enUS,
};

const STORAGE_KEY = 'xico.locale';

function readInitialLocale(): Locale {
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'es-MX' || stored === 'en-US') return stored;
  }
  // D-009: en-US is the default; es-MX stays one tap away in the switcher.
  return 'en-US';
}

export interface Formatters {
  /** Cents in an explicit ISO currency (money spine: USD credits, license totals). */
  money: (cents: number, currency: string) => string;
  /** Milliseconds → "2:34". */
  duration: (ms: number | null | undefined) => string;
  /** Milliseconds → "1:24.350" (for cut-point precision). */
  durationMs: (ms: number) => string;
  /** BPM number → "140 BPM" (empty if null). */
  bpm: (value: number | null | undefined) => string;
  /** Bytes → "18.4 MB". */
  bytes: (n: number | null | undefined) => string;
  /** ISO string → localized short date. */
  date: (iso: string | null | undefined) => string;
  /** Plain integer with locale grouping. */
  int: (n: number) => string;
}

interface I18nValue {
  locale: Locale;
  t: Dictionary;
  setLocale: (l: Locale) => void;
  toggleLocale: () => void;
  fmt: Formatters;
}

const I18nContext = createContext<I18nValue | null>(null);

function buildFormatters(locale: Locale): Formatters {
  const intFmt = new Intl.NumberFormat(locale);
  const dateFmt = new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short', year: 'numeric' });

  const pad = (n: number, len = 2) => String(n).padStart(len, '0');

  const moneyFmts = new Map<string, Intl.NumberFormat>();
  const moneyFmt = (currency: string) => {
    // Never crash a money render over a missing/blank currency (e.g. a credits
    // pack or listing row without price_currency) — default to USD (D-010).
    const key = (currency || 'USD').toUpperCase();
    let f = moneyFmts.get(key);
    if (!f) {
      try {
        f = new Intl.NumberFormat(locale, { style: 'currency', currency: key, currencyDisplay: 'narrowSymbol' });
      } catch {
        // Unknown ISO code from the API — never crash a money render over formatting.
        f = new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD', currencyDisplay: 'narrowSymbol' });
      }
      moneyFmts.set(key, f);
    }
    return f;
  };

  return {
    money: (cents, currency) => moneyFmt(currency).format(cents / 100),
    duration: (ms) => {
      if (ms == null || !Number.isFinite(ms)) return '—:—';
      const total = Math.max(0, Math.round(ms / 1000));
      return `${Math.floor(total / 60)}:${pad(total % 60)}`;
    },
    durationMs: (ms) => {
      const safe = Math.max(0, Math.round(ms));
      const min = Math.floor(safe / 60000);
      const sec = Math.floor((safe % 60000) / 1000);
      const millis = safe % 1000;
      return `${min}:${pad(sec)}.${pad(millis, 3)}`;
    },
    bpm: (value) => (value == null ? '' : `${Math.round(value)} BPM`),
    bytes: (n) => {
      if (n == null || !Number.isFinite(n)) return '—';
      if (n < 1024) return `${n} B`;
      const units = ['KB', 'MB', 'GB'];
      let val = n / 1024;
      let u = 0;
      while (val >= 1024 && u < units.length - 1) {
        val /= 1024;
        u += 1;
      }
      return `${val.toFixed(val >= 10 ? 0 : 1)} ${units[u]}`;
    },
    date: (iso) => {
      if (!iso) return '';
      const d = new Date(iso);
      return Number.isNaN(d.getTime()) ? '' : dateFmt.format(d);
    },
    int: (n) => intFmt.format(n),
  };
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readInitialLocale);

  useEffect(() => {
    document.documentElement.lang = locale;
    if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, locale);
  }, [locale]);

  const setLocale = useCallback((l: Locale) => setLocaleState(l), []);
  const toggleLocale = useCallback(() => setLocaleState((l) => (l === 'es-MX' ? 'en-US' : 'es-MX')), []);

  const value = useMemo<I18nValue>(
    () => ({
      locale,
      t: DICTIONARIES[locale],
      setLocale,
      toggleLocale,
      fmt: buildFormatters(locale),
    }),
    [locale, setLocale, toggleLocale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}
